import React from 'react';

import { Container } from '@mui/material';

import AreasBlock from '../../components/AreasBlock/AreasBlock';
import { CardsBlock } from '../../components/CardsBlock';
import { ContactDetailsBlock } from '../../components/ContactDetailsBlock';
import { ContactFormBlock } from '../../components/ContactFormBlock';
import ContentListBlock from '../../components/ContentListBlock/ContentListBlock';
import { CtaBlock } from '../../components/CtaBlock';
import { ExpertsBlock } from '../../components/ExpertsBlock';
import { HTMLBlock } from '../../components/HTMLBlock';
import HalfWidthImgBlock from '../../components/HalfWidthImgBlock';
import { ImageHeadingTextBlock } from '../../components/ImageHeadingTextBlock';
import { ImageTextArrayBlock } from '../../components/ImageTextArrayBlock';
import ImageWithTabsBlock from '../../components/ImageWithTabsBlock';
import { IntroBlock } from '../../components/IntroBlock';
import { JSBlock } from '../../components/JSBlock';
import MeetTheTeam from '../../components/MeetTheTeam/MeetTheTeam';
import { NewsBlock } from '../../components/NewsBlock';
import { PartnersBlock } from '../../components/PartnersBlock';
import { PathwaysListBlock } from '../../components/PathwaysListBlock';
import { QuotesBlock } from '../../components/QuotesBlock';
import { SeoBlock } from '../../components/SeoBlock';
import TabsSliderBlock from '../../components/TabsSliderBlock/TabsSliderBlock';
import { TrustpilotBlock } from '../../components/TrustpilotBlock';
import VideosBlock from '../../components/VideosBlock/VideosBlock';
import { WeAreBlock } from '../../components/WeAreBlock';
import WhyUsWideImgBlock from '../../components/WhyUsWideImgBlock';
import { PricePage } from '../../pages/PricePage';
import { Block, IntroImageBlockViewType, LearnerInfo, Tenant } from '../../types';
import { useAuth } from '../../utils/hooks';
import AccordionsBlock from '../AccordionsBlock/AccordionsBlock';
import CoursesBlock from '../CoursesBlock/CoursesBlock';
import ImageAndTextBlock from '../ImageAndTextBlock/ImageAndTextBlock';
import RequestADemo from '../RequestADemo/RequestADemo';
import SearchBlock from '../SearchBlock/SearchBlock';
import Testimonials from '../Testimonials/Testimonials';
import useStyles from './styles';

const BLOCKS = [
  {
    type: 'intro-video-block',
    Component: IntroBlock,
  },
  {
    type: 'intro-image-block',
    view_type: IntroImageBlockViewType.Intro,
    Component: (data) => <IntroBlock {...data} video={false} />,
  },
  {
    type_image: 'fixed_image',
    Component: ImageHeadingTextBlock,
  },
  {
    type_image: 'wide_image',
    Component: WhyUsWideImgBlock,
  },
  {
    type: 'images-and-text-array-block',
    Component: ImageTextArrayBlock,
  },
  {
    type: 'partners',
    Component: PartnersBlock,
  },
  {
    type: 'newslayout',
    Component: NewsBlock,
  },
  {
    type: 'videolist',
    Component: VideosBlock,
  },
  {
    type: 'pathwaylist',
    Component: PathwaysListBlock,
  },
  {
    type: 'catalogue-of-categories',
    Component: AreasBlock,
  },
  {
    type: 'experts',
    Component: ExpertsBlock,
  },
  {
    type: 'contact-details-block',
    Component: ContactDetailsBlock,
  },
  {
    type: 'contact-form-block',
    Component: ContactFormBlock,
  },
  {
    type: 'seo-block',
    Component: SeoBlock,
  },
  {
    type: 'pricingblock',
    Component: (data) => <PricePage {...data} isBlock={true} />,
  },
  {
    type: 'html-block',
    Component: HTMLBlock,
  },
  {
    type: 'js-block',
    Component: JSBlock,
  },
  {
    type: 'quotes-list-block',
    Component: QuotesBlock,
  },
  {
    type: 'cta-block',
    Component: CtaBlock,
  },
  {
    type: 'for-enterprise',
    Component: ImageWithTabsBlock,
  },
  {
    type: 'all-professionals',
    Component: HalfWidthImgBlock,
  },
  {
    type: 'trustpilot-block',
    Component: TrustpilotBlock,
  },
  {
    type: 'image-slider-with-text',
    Component: TabsSliderBlock,
  },
  {
    type: 'what-businesses-stand-to-gain',
    Component: CardsBlock,
  },
  {
    type: 'meet-the-team',
    Component: MeetTheTeam,
  },
  {
    type: 'we-are-block',
    Component: WeAreBlock,
  },
  {
    type: 'testimonials',
    Component: Testimonials,
  },
  {
    type: 'faq',
    Component: AccordionsBlock,
  },
  {
    type: 'request_demo',
    Component: RequestADemo,
  },
  {
    type: 'intro-image-block',
    view_type: IntroImageBlockViewType.Inline,
    Component: ImageAndTextBlock,
  },
  {
    type: 'courselist',
    Component: CoursesBlock,
  },
  {
    type: 'search_block',
    Component: SearchBlock,
  },
  {
    type: 'content-item-list',
    Component: ContentListBlock,
  },
];

interface CustomBlocksProps {
  blocks: (Block | null)[] | null;
  tenant?: Tenant | null;
  userInfo?: LearnerInfo;
  preventLoader?: boolean;
  blocksLoading?: boolean;
}

const CustomBlocks: React.FC<CustomBlocksProps> = ({
  blocks,
  preventLoader,
  tenant,
  userInfo,
  blocksLoading,
}) => {
  const { isInitialized } = useAuth();
  const blocksNotLoaded = !isInitialized || blocksLoading;
  const classes = useStyles(blocksNotLoaded)();

  let blocksArray: any[] = [];

  if (blocks) {
    // eslint-disable-next-line
    for (let [key, value] of Object.entries(blocks)) {
      blocksArray.push({ ...value });
    }
  }

  // For "images-and-text-array-block" we need to check their ranking
  // to show them differently if they go one after each other
  let counter = 0;
  let startFromImage = false;

  blocksArray.forEach((block) => {
    if (block?.type === 'images-and-text-array-block') {
      if (counter === 0 && block?.items?.length && block.items[0]?.type === 'image') {
        startFromImage = true;
      }
      counter += 1;
      block.shouldStartFromImage = startFromImage ? Boolean(counter % 2) : !(counter % 2);
    } else {
      counter = 0;
      startFromImage = false;
    }
  });

  return (
    <Container className={classes.container} maxWidth={false}>
      {blocksArray.map((block, index) => {
        const desiredBlock = BLOCKS.find((item) =>
          block.type_image
            ? item.type_image === block.type_image
            : item.type === block.type &&
              (item?.view_type ? block?.view_type === item?.view_type : true),
        );
        const Component = desiredBlock?.Component;

        if (!Component) return null;

        return (
          <Component
            key={index}
            data={block}
            tenant={tenant}
            userInfo={userInfo}
            preventLoader={preventLoader}
          />
        );
      })}
    </Container>
  );
};

export default CustomBlocks;
